import React, { useContext, useEffect, useReducer, useState } from "react";
import MaterialTable from "material-table";
import {
    Typography,
    Button,
    IconButton,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import HistoryIcon from "@material-ui/icons/History";
import tableIcons, { options } from "components/universal/table_attributes";
import { ToastMessageContext } from "lib/contexts/message_context";
import TableAction from "actions/table_actions";
import tableReducer, { INITIAL_TABLE_STATE } from "reducers/table_reducer";
import { format24HrDateOnly, formatDate } from "lib/utils/common_utils";
import { ICON_COLOUR } from "lib/constants/style_constants";
import { BASE_API_URL_BOXR } from "lib/constants";
import HistoryTablePopup from "views/Compliance/UserCompliance/UserDetails/HistoryPopup";

function Training({ title, section, id }) {
    const message = useContext(ToastMessageContext);
    const [tableState, dispatch] = useReducer(tableReducer, INITIAL_TABLE_STATE);
    const { data, inProgress } = tableState;

    const [popupOpen, setPopupOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isTrainingHistory, setIsTrainingHistory] = useState(false);

    // --------------------------
    // CONSTANTS
    // --------------------------
    const statusMapButtons = {
        "On Track": "button-positive",
        "Near Due": "button-warning",
        Overdue: "button-negative",
    };
    const statusMap = {
        "On Track": "text-positive",
        "Near Due": "text-warning",
        Overdue: "text-negative",
    };

    const tableOptions = {
        ...options,
        search: false,
        paging: true,
        pageSize: 5,
        toolbar: false
    };

    // --------------------------
    // EFFECTS
    // --------------------------
    useEffect(() => {
        if (id) fetchDocument();
    }, [id]);

    const fetchDocument = () => {
        const params = { user_id: id };
        TableAction.getList(dispatch, message, params, section);
    };

    // --------------------------
    // HANDLERS
    // --------------------------
    const viewDoc = (doc) => {
        if (!doc) {
            message.showToastMessage({
                open: true,
                message: "No document found",
                variant: "error",
            });
            return;
        }
        const url = `${process.env.REACT_APP_BASE_API_URL_BOXR || BASE_API_URL_BOXR}${doc}`;
        window.open(url, "_blank");
    };

    const openHistory = (row, isTraining = false) => {
        setSelectedRow(row);
        setIsTrainingHistory(isTraining);
        setPopupOpen(true);
    };

    const closeHistory = () => setPopupOpen(false);

    // --------------------------
    // COLUMNS
    // --------------------------
    const columns = [
        {
            title: "",
            field: "id",
            width: "4%",
            render: () => <span>&nbsp;</span>,
        },
        {
            title: "Name",
            field: "name",
            headerStyle: { textAlign: "left" },
            cellStyle: { textAlign: "left", padding: "15px 0" },
        },
        {
            title: "Expiry",
            field: "expiry",
            render: (rowData) => {
                const expiry =
                    title === "Competency"
                        ? rowData?.assessment?.expiry
                        : rowData?.expiry;
                const status =
                    title === "Competency"
                        ? rowData?.assessment?.status
                        : rowData?.status;

                return (
                    <span className={expiry !== "-" && statusMap[status]}>
                        <b>
                            {title === "Competency"
                                ? formatDate(expiry)
                                : format24HrDateOnly(expiry) || "-"}
                        </b>
                    </span>
                );
            },
        },
        {
            title: "Status",
            field: "status",
            render: (rowData) => {
                const status =
                    title === "Competency"
                        ? rowData?.assessment?.status
                        : rowData?.status;
                return (
                    <Button
                        size="small"
                        className={status && statusMapButtons[status]}
                        variant="outlined"
                    >
                        <b>{status || "-"}</b>
                    </Button>
                );
            },
        },
        {
            title: "File",
            field: "file",
            render: (rowData) => {
                const doc =
                    title === "Competency"
                        ? rowData?.assessment?.document
                        : rowData?.certificate;
                return (
                    <>
                        <IconButton onClick={() => viewDoc(doc)}>
                            <VisibilityIcon fontSize="small" htmlColor={ICON_COLOUR} />
                        </IconButton>
                        <IconButton
                            onClick={() => openHistory(rowData, title === "Training")}
                        >
                            <HistoryIcon />
                        </IconButton>
                    </>
                );
            },
        },
    ];


    return (
        <div >
            <MaterialTable
                style={{
                    boxShadow: "0px 1px 3px #00000033",
                }}
                icons={tableIcons}
                title={
                    <Typography variant="subtitle2" color="secondary">
                        {title}
                    </Typography>
                }
                columns={columns}
                data={data?.data || []}
                isLoading={inProgress}
                options={tableOptions}
                localization={{
                    body: { emptyDataSourceMessage: `No ${title} Found` },
                }}
            />

            {popupOpen && (
                <HistoryTablePopup
                    busy={busy}
                    open={popupOpen}
                    handleClose={closeHistory}
                    currentData={selectedRow}
                    name={isTrainingHistory ? "training_id" : "competency_user_id"}
                />
            )}
        </div>
    );
}

export default Training;
